import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import ViewMode from "@/utils/constants/ViewMode.ts";
import router from "@/router/index.ts"
import RouteNames from "@/router/RouteNames.ts";
import EditPlace from "@/utils/presenters/EditPlace.ts";
import PlaceTokens from "@/utils/constants/PlaceTokens.ts";
import CommonFieldIds from "@/utils/constants/CommonFieldIds.ts";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import NamaPlace from "@/utils/presenters/NamaPlace.ts";
import type {LocationQueryRaw} from "vue-router";
import CommonConstants from "@/utils/constants/CommonConstants.ts";
import {InDevMode} from "@/devmode.ts";

export default class PlaceUtils {
  static linkToObject(object?: Record<string, any>) {
    if (ObjectChecker.isEmptyOrNull(object))
      return "";
    let editPlace = this.editPlace(object[CommonFieldIds.ENTITY_TYPE], object[CommonFieldIds.ID]);
    if (editPlace == null)
      return "";
    return this.linkToPlace(editPlace);
  }

  static linkToReport(object: Record<string, any>) {
    let resolve = router.resolve({
      name: RouteNames.LAUNCH_REPORT_ROUTE,
      query: {
        id: object[CommonFieldIds.ID]
      }
    });
    return resolve.href;
  }

  static linkToPlace(place: NamaPlace) {
    let resolve = router.resolve({
      name: RouteNames.EDIT_VIEW_ROUTE,
      query: place.fetchQuery()
    });
    return resolve.href;
  }

  static editLink(entityType: string | undefined, id: string | undefined, object?: object): string | undefined {
    if (!id)
      return;
    let place = this.editPlace(entityType, id, object);
    if (!place)
      return;
    return this.linkToPlace(place);
  }

  static editPlace(entityType: string | undefined, id: string | undefined, object?: object): EditPlace | undefined {
    if (ObjectChecker.isEmptyOrNull(entityType))
      return;
    let editPlace: EditPlace = this.createEditPlace(entityType!, id, ViewMode.EDIT);
    if (object) {
      localStorage.setItem(object[CommonFieldIds.ID], NamaCommon.stringify(object));
      editPlace.createdObjectId = object[CommonFieldIds.ID];
    }
    return editPlace;
  }

  static newPlace(entityType: string | undefined, object?: object): EditPlace | undefined {
    if (ObjectChecker.isEmptyOrNull(entityType))
      return;
    let editPlace: EditPlace = this.createEditPlace(entityType!, undefined, ViewMode.NEW);
    if (object) {
      localStorage.setItem(object[CommonFieldIds.ID], NamaCommon.stringify(object));
      editPlace.createdObjectId = object[CommonFieldIds.ID];
    }
    return editPlace;
  }

  static linkToNew(entityType: string, requesterType?: string): NamaPlace {
    const editPlace = this.newPlace(entityType);
    editPlace!.quickCreateRequester = requesterType;
    return editPlace as NamaPlace;
  }

  static createEditPlace(entityType: string, id: string | undefined, edit: string): EditPlace {
    let currentRoute = router.currentRoute.value;
    if (currentRoute.name === RouteNames.LIST_VIEW_ROUTE && ObjectChecker.areEqual(currentRoute.query[PlaceTokens.PRM_ENTITY_TYPE], entityType))
      return EditPlace.createInstance(entityType, id, edit, NamaCommon.cloneObject(currentRoute.query as Record<string, string>));
    return EditPlace.createInstance(entityType, id, edit);
  }

  static getQueryFromUrl(link: string | undefined): LocationQueryRaw | null {
    if (!link)
      return null;
    const routePath = this.getRoutePath(link);
    if (!routePath || link.length == routePath?.length)
      return null;
    const hashParams = link.substring(link.indexOf(routePath) + routePath.length + 1)  // : or ?
    const params = hashParams.split("&").reduce((acc, param) => {
      const [key, value] = param.split(/[\/=]/) // = or /
      if (key)
        acc[key] = value;
      return acc;
    }, {})
    if (ObjectChecker.isEmptyOrNull(params) || ObjectChecker.isEmptyObject(params))
      return null;
    else
      return params;
  }

  // #edit or /edit
  static getRouteToken(link) {
    let tokens = [PlaceTokens.PREFIX_LIST, PlaceTokens.PREFIX_EDIT, PlaceTokens.PREFIX_LAUNCH_REPORT, PlaceTokens.PREFIX_REPORT_Result,
      PlaceTokens.PREFIX_REPORT_List, PlaceTokens.PREFIX_WELCOME, PlaceTokens.WIZARD_VIEW]
    const currentToken = tokens.find(t => {
      const regex = new RegExp(`[/#]${t}(?=[:?]|$)`); //  #edit? or /edit:
      if (!regex.test(link))
        return;
      return t;
    })
    if (!currentToken)
      return null;
    else
      return currentToken;
  }

  // localhost:8080/vuedev.html#/edit or daralamirat.namasoft.com/erp/erp1.html#list
  static getRoutePath(link: string): string | null {
    let routeToken = this.getRouteToken(link);
    if (!routeToken)
      return null
    else
      return link.slice(0, link.indexOf(routeToken) + routeToken.length);
  }

  static normalizedGUILink(link: string | undefined): string | undefined {
    if (ObjectChecker.isEmptyOrNull(link))
      return;
    let actualUrl = link;
    const separator = CommonConstants.URL_TITLE_SEPARATOR;
    if (actualUrl.includes(separator))
      return actualUrl.substring(actualUrl.indexOf(separator) + separator.length)
    const routeToken = this.getRouteToken(actualUrl);
    const query = this.getQueryFromUrl(actualUrl);
    if (ObjectChecker.areAllEmptyOrNull(query, routeToken))
      return router.resolve(actualUrl).fullPath;
    else
      return router.resolve({
        query: query ?? {},
        path: routeToken ?? "",
      }).href;
  }
}
